import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { dmYhm } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, Warning, CheckCircle, Database, ClockClockwise, Shield } from "@phosphor-icons/react";

const HUMAN_LABELS = {
    parties: "Parties",
    pricing_rules: "Pricing Rules",
    company_upis: "Company UPIs",
    payment_modes: "Payment Modes",
    transactions: "Transactions",
    uploads: "Uploaded Slip Metadata",
    users: "Users (Admins + Customers)",
};

export default function DataMigration() {
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [previewCounts, setPreviewCounts] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [backups, setBackups] = useState([]);
    const [backingUp, setBackingUp] = useState(false);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);

    const runBackfill = async () => {
        if (!window.confirm("Save every uploaded slip image to permanent MongoDB storage? This makes them survive future re-publishes. Safe to run anytime.")) return;
        setBackfilling(true);
        setBackfillResult(null);
        try {
            // 1) Rewrite any legacy /api/uploads/... URLs → /api/uploads-file/<id>
            await api.post("/admin/uploads/rewrite-urls");
            // 2) Copy every disk-only slip into GridFS
            const { data } = await api.post("/admin/uploads/backfill-gridfs");
            setBackfillResult(data);
            toast.success(`Slip files backfill complete — ${data.migrated} moved to permanent storage`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBackfilling(false);
        }
    };

    const loadBackups = () => {
        api.get("/admin/backups").then((r) => setBackups(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    };

    useEffect(() => { loadBackups(); }, []);

    const triggerBackup = async () => {
        setBackingUp(true);
        try {
            const { data } = await api.post("/admin/backups/create");
            toast.success(`Backup saved: ${data.name} (${(data.size_bytes / 1024).toFixed(1)} KB)`);
            loadBackups();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBackingUp(false);
        }
    };

    const downloadBackup = async (name) => {
        try {
            const resp = await api.get(`/admin/backups/${encodeURIComponent(name)}`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${name}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const doExport = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/admin/data-export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
            a.download = `micm-export-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Exported ${Object.values(data.counts || {}).reduce((s, n) => s + n, 0)} records — file downloaded`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setExporting(false);
        }
    };

    const onFilePick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFile(file);
        setImportResult(null);
        try {
            const text = await file.text();
            const payload = JSON.parse(text);
            const counts = {};
            Object.keys(HUMAN_LABELS).forEach((k) => {
                counts[k] = Array.isArray(payload[k]) ? payload[k].length : 0;
            });
            setPreviewCounts(counts);
            setPendingPayload(payload);
        } catch (err) {
            toast.error("Invalid JSON file — please pick a valid MICM export");
            setSelectedFile(null);
            setPreviewCounts(null);
            setPendingPayload(null);
        }
    };

    const doImport = async () => {
        if (!pendingPayload) return;
        const total = Object.values(previewCounts || {}).reduce((s, n) => s + n, 0);
        if (!window.confirm(`Import ${total} records into THIS environment? Existing records (matched by id or party name) will be skipped, not overwritten.`)) return;
        setImporting(true);
        setImportResult(null);
        try {
            const { data } = await api.post("/admin/data-import", pendingPayload);
            setImportResult(data);
            toast.success(`Imported ${data.total_inserted} new records`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setImporting(false);
        }
    };

    return (
        <div data-testid="data-migration-page" className="max-w-4xl space-y-6">
            <div>
                <h1 className="font-display text-2xl font-bold text-slate-900">Data Migration &amp; Backups</h1>
                <p className="mt-1 text-sm text-slate-500">Move records between environments (preview ↔ production) and manage automatic backups so your work is never lost.</p>
            </div>

            {/* Automatic backups section */}
            <div className="rounded-xl border border-emerald-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                            <Shield size={18} weight="bold" />
                        </div>
                        <div>
                            <h2 className="font-display text-lg font-semibold text-slate-900">Automatic backups</h2>
                            <p className="text-xs text-slate-500">The server saves a full snapshot every 24 hours and keeps the last 7. You can also trigger one now.</p>
                        </div>
                    </div>
                    <Button
                        data-testid="trigger-backup-btn"
                        onClick={triggerBackup}
                        disabled={backingUp}
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                        <ClockClockwise size={16} className="mr-2" />
                        {backingUp ? "Saving…" : "Backup now"}
                    </Button>
                </div>
                {backups.length === 0 ? (
                    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
                        No backups yet — click "Backup now" or wait for the first auto-snapshot.
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-md border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold">File</th>
                                    <th className="px-3 py-2 text-left font-semibold">Created</th>
                                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                                    <th className="px-3 py-2 text-right font-semibold">Size</th>
                                    <th className="px-3 py-2 text-right font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody data-testid="backup-list">
                                {backups.map((b) => (
                                    <tr key={b.name} className="border-t border-slate-100">
                                        <td className="px-3 py-2 font-mono text-xs text-slate-800">{b.name}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{dmYhm(b.created_at)}</td>
                                        <td className="px-3 py-2 text-xs">
                                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.auto ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                                {b.auto ? "AUTO" : "MANUAL"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-600">{(b.size_bytes / 1024).toFixed(1)} KB</td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                type="button"
                                                data-testid={`download-backup-${b.name}`}
                                                onClick={() => downloadBackup(b.name)}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                                            >
                                                <Download size={13} /> Download
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                    <Warning size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-900">
                        <div className="font-semibold">Read before importing</div>
                        <ul className="mt-1 list-inside list-disc space-y-1 text-amber-800">
                            <li>Physical files (slip images/PDFs) are NOT transferred — only metadata rows. Old images must be re-uploaded on the target environment if needed.</li>
                            <li>Import is <strong>additive</strong> — records with an existing <code>id</code> or matching party name are skipped, never overwritten.</li>
                            <li>Recommend: run this migration once, in a low-traffic window. Log out of the target site after import so JWT / session data refresh cleanly.</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Slip file backfill section */}
            <div className="rounded-xl border border-blue-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 text-white shadow-sm">
                            <Database size={18} weight="bold" />
                        </div>
                        <div>
                            <h2 className="font-display text-lg font-semibold text-slate-900">Protect slip files from redeploys</h2>
                            <p className="text-xs text-slate-500">Copies every uploaded slip image from local disk (temporary) into MongoDB (permanent). Runs once — safe to re-run anytime.</p>
                        </div>
                    </div>
                    <Button
                        data-testid="run-backfill-btn"
                        onClick={runBackfill}
                        disabled={backfilling}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <Shield size={16} className="mr-2" />
                        {backfilling ? "Protecting…" : "Protect slip files"}
                    </Button>
                </div>
                {backfillResult && (
                    <div data-testid="backfill-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                        <div className="font-semibold">Backfill complete</div>
                        <div className="mt-1">
                            <span className="mr-4">Moved to permanent storage: <strong>{backfillResult.migrated}</strong></span>
                            <span className="mr-4">Already permanent: skipped</span>
                            <span className="mr-4">Missing on disk (lost): <strong>{backfillResult.missing_on_disk}</strong></span>
                            {backfillResult.errors > 0 && <span>Errors: <strong>{backfillResult.errors}</strong></span>}
                        </div>
                        {backfillResult.missing_on_disk > 0 && (
                            <div className="mt-2 text-amber-800">
                                {backfillResult.missing_on_disk} slip file(s) are gone from disk. Those slips must be re-uploaded manually — their ledger balances are still safe.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Export section */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-sm">
                        <Download size={18} weight="bold" />
                    </div>
                    <div>
                        <h2 className="font-display text-lg font-semibold text-slate-900">Step 1 · Export from source</h2>
                        <p className="text-xs text-slate-500">Downloads a single JSON file with every business record from THIS environment.</p>
                    </div>
                </div>
                <Button
                    data-testid="export-data-btn"
                    onClick={doExport}
                    disabled={exporting}
                    className="bg-sky-600 text-white hover:bg-sky-700"
                >
                    <Download size={16} className="mr-2" />
                    {exporting ? "Exporting…" : "Download JSON export"}
                </Button>
            </div>

            {/* Import section */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-sm">
                        <Upload size={18} weight="bold" />
                    </div>
                    <div>
                        <h2 className="font-display text-lg font-semibold text-slate-900">Step 2 · Import into target</h2>
                        <p className="text-xs text-slate-500">Pick the JSON file you exported from the other environment. Existing records will be skipped, not overwritten.</p>
                    </div>
                </div>

                <label className="mb-3 block">
                    <input
                        data-testid="import-file-input"
                        type="file"
                        accept="application/json"
                        onChange={onFilePick}
                        className="block w-full text-sm text-slate-600 file:mr-4 file:cursor-pointer file:rounded-md file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-emerald-700 hover:file:bg-emerald-100"
                    />
                </label>

                {selectedFile && previewCounts && (
                    <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                            <Database size={16} weight="bold" /> {selectedFile.name} · {(selectedFile.size / 1024).toFixed(1)} KB
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                            {Object.entries(previewCounts).map(([k, v]) => (
                                <div key={k} className="rounded border border-slate-200 bg-white px-2 py-1.5">
                                    <div className="text-slate-500">{HUMAN_LABELS[k]}</div>
                                    <div className="font-semibold text-slate-900">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <Button
                    data-testid="import-data-btn"
                    onClick={doImport}
                    disabled={!pendingPayload || importing}
                    className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                    <Upload size={16} className="mr-2" />
                    {importing ? "Importing…" : "Merge into this environment"}
                </Button>
            </div>

            {/* Import result */}
            {importResult && (
                <div data-testid="import-result" className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="mb-3 flex items-center gap-2 text-emerald-800">
                        <CheckCircle size={20} weight="fill" />
                        <h3 className="font-display text-lg font-semibold">Import complete — {importResult.total_inserted} new records</h3>
                    </div>
                    <div className="overflow-hidden rounded-md border border-emerald-200 bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-emerald-100/60 text-xs uppercase tracking-wider text-emerald-800">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold">Collection</th>
                                    <th className="px-3 py-2 text-right font-semibold">In file</th>
                                    <th className="px-3 py-2 text-right font-semibold">Inserted</th>
                                    <th className="px-3 py-2 text-right font-semibold">Skipped</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(importResult.results || {}).map(([k, r]) => (
                                    <tr key={k} className="border-t border-slate-100">
                                        <td className="px-3 py-2 text-slate-800">{HUMAN_LABELS[k] || k}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.total}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{r.inserted}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.skipped}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-3 text-xs text-emerald-800">Tip: hard-refresh (Ctrl+Shift+R) or reopen the PWA to see the newly imported data on the Dashboard.</p>
                </div>
            )}
        </div>
    );
}
